import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import arrowBackIcon from "../../img/arrow_back_ios-black-36dp.svg";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPassword = ({ baseUrl }) => {
  const query = useQuery();
  const token = query.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [confirmError, setConfirmError] = useState(false);
  const [confirmErrorMessage, setConfirmErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    let hasError = false;
    setPasswordError(false);
    setPasswordErrorMessage("");
    setConfirmError(false);
    setConfirmErrorMessage("");
    setError("");

    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return false;
    }
    if (password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Password must be at least 6 characters.");
      hasError = true;
    }
    if (!confirmPassword) {
      setConfirmError(true);
      setConfirmErrorMessage("Please confirm your password.");
      hasError = true;
    }
    if (hasError) return false;

    if (password !== confirmPassword) {
      setConfirmError(true);
      setConfirmErrorMessage("Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setError("");
    try {
      const url = `${baseUrl}/api/v1/auth/reset-password`;
      const response = await fetch(url, {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json().catch(() => null);
        setError(
          data?.message ||
            "Failed to reset password. The link may have expired."
        );
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center">
          <div className="flex w-full flex-1 items-center">
            <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center">
              <div className="mb-2 flex justify-center">
                <svg className="h-12 w-12 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="mb-3 text-2xl font-semibold text-slate-900">
                Password Reset
              </div>
              <p className="mb-6 text-sm text-slate-600">
                Your password has been successfully updated. You can now sign in
                with your new password.
              </p>
              <Link
                to="/login"
                className="inline-block w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center">
          <div className="flex w-full flex-1 items-center">
            <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center">
              <div className="mb-2 flex justify-center">
                <svg className="h-12 w-12 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="mb-3 text-2xl font-semibold text-slate-900">
                Invalid Link
              </div>
              <p className="mb-6 text-sm text-slate-600">
                This password reset link is invalid or has expired. Please
                request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Request New Link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const submitButton = (
    <span className="flex items-center justify-center gap-2">
      {isLoading && (
        <svg
          className="h-4 w-4 animate-spin text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      <span>Reset Password</span>
    </span>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center">
        <div className="mb-4 flex w-full justify-start">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <img src={arrowBackIcon} alt="" className="h-5 w-5" />
            Back to Login
          </Link>
        </div>
        <div className="flex w-full flex-1 items-center">
          <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-2 text-center text-2xl font-semibold text-slate-900">
              Set New Password
            </div>
            <p className="mb-6 text-center text-sm text-slate-600">
              Enter your new password below.
            </p>

            <div className="space-y-4">
              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New Password"
                    className={`w-full rounded-md border px-3 py-2 pr-10 text-sm outline-none focus:border-slate-500 ${
                      passwordError ? "border-red-500" : "border-slate-300"
                    }`}
                    autoFocus
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError(false);
                      setPasswordErrorMessage("");
                    }}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordErrorMessage && (
                  <div className="mt-1 text-sm text-red-600">{passwordErrorMessage}</div>
                )}
              </div>

              <div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-slate-500 ${
                    confirmError ? "border-red-500" : "border-slate-300"
                  }`}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setConfirmError(false);
                    setConfirmErrorMessage("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSubmit();
                  }}
                />
                {confirmErrorMessage && (
                  <div className="mt-1 text-sm text-red-600">{confirmErrorMessage}</div>
                )}
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <button
                type="button"
                disabled={isLoading}
                className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={handleSubmit}
              >
                {submitButton}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
